import React, { useEffect, useState } from "react";
import {
  Box,
  Button,
  Table,
  TableBody,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  useMediaQuery,
  useTheme,
  TableCell as MuiTableCell,
  styled,
  Tooltip,
  TablePagination,
} from "@mui/material";
import useAccessCode from "../hooks/useAccessCode";
import { IHistoryIncidentes, IHistoryIncidentesMessages, IHistoryReservaciones } from "../models/codes";
import { CustomModal } from "../components/Modal";
import { Done, DoneAll, Home, Message, Image, Search } from "@mui/icons-material";
import { TableHeaderCell } from "../components/Table/TableHeaderCell";
import { TableCell } from "../components/Table/TableCell";
import { Confirm } from '../components/Confirm';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import TextField from '@mui/material/TextField';
import InputAdornment from '@mui/material/InputAdornment';
import IconButton from '@mui/material/IconButton';
import CloseIcon from '@mui/icons-material/Close';
import { useAppSelector } from "../redux/hooks";

const MessageIcon = Message;
const ImageIcon = Image;

type ChatMessage = Omit<IHistoryIncidentesMessages, 'tipo'> & { tipo: string };

interface ChatModalProps {
  open: boolean;
  onClose: () => void;
  messages: ChatMessage[];
  onSend: (text: string) => void;
  ticketNumber?: string | number;
  estado?: string;
}

const ChatModal = ({ open, onClose, messages, onSend, ticketNumber, estado }: ChatModalProps) => {
  const [input, setInput] = React.useState("");

  const puedeEnviar = estado ? estado.toLowerCase() !== 'cerrado' : true;
  //const puedeEnviar = true;

  const handleSend = () => {
    if (input.trim() === "") return;
    if (!puedeEnviar) return;
    onSend(input);
    setInput("");
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        Chat del incidente{ticketNumber ? ` ${ticketNumber}` : ''}
      </DialogTitle>
      <DialogContent dividers>
        <Box sx={{ maxHeight: 400, overflowY: 'auto' }}>
          {messages.length === 0 && (
            <Typography variant="body2" color="text.secondary">No hay mensajes.</Typography>
          )}
          {messages.map((msg, idx) => (
            <Box
              key={idx}
              mb={2}
              display="flex"
              flexDirection="row"
              justifyContent={String(msg.tipo) === "2" ? 'flex-start' : 'flex-end'}
            >
              <Box
                sx={{
                  bgcolor: String(msg.tipo) === "2" ? 'grey.200' : 'primary.main',
                  color: String(msg.tipo) === "2" ? 'text.primary' : 'primary.contrastText',
                  borderRadius: 2,
                  px: 2,
                  py: 1,
                  maxWidth: '80%',
                  boxShadow: 1,
                  alignSelf: String(msg.tipo) === "3" ? 'flex-start' : 'flex-end',
                }}
              >
                <Typography variant="caption" color="body2">
                  {msg.fecha}
                </Typography>
                <Typography variant="body2">
                  {msg.nota}
                </Typography>
              </Box>
            </Box>
          ))}
        </Box>
      </DialogContent>
      <Box sx={{ p: 2, pt: 1 }}>
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <TextField
            fullWidth
            size="small"
            placeholder="Escribe un mensaje..."
            value={input}
            onChange={e => setInput(e.target.value)}
            onKeyDown={handleKeyDown}
            multiline
            maxRows={3}
            disabled={!puedeEnviar}
          />
          <Button onClick={handleSend} color="primary" variant="contained" sx={{ ml: 1 }} disabled={!puedeEnviar}>
            Enviar
          </Button>
        </Box>
        {!puedeEnviar && (
          <Typography variant="caption" color="error" sx={{ mt: 1, width: '100%', textAlign: 'center' }}>
            Ticket cerrado, no se pueden enviar mensajes.
          </Typography>
        )}
      </Box>
      <DialogActions>
        <Button onClick={onClose} color="primary">Cerrar</Button>
      </DialogActions>
    </Dialog>
  );
};

export const HistorialIncidentes = () => {
  const [confirm, setConfirm] = useState<boolean>(false);
  const today = new Date();
  const theme = useTheme()
  const isSmallScreen = useMediaQuery(theme.breakpoints.down('sm'))
  today.setHours(0, 0, 0, 0);

  const MobileTableCell = styled(MuiTableCell)(({ theme }) => ({
    padding: '4px 6px',
    fontSize: '0.7rem',
    lineHeight: '1.1',
    '&:first-of-type': {
      paddingLeft: '6px'
    },
    '&:last-of-type': {
      paddingRight: '6px'
    }
  }))

  const { getHistoryIncidentes, getMessagesTicket, sendMessageTicket } = useAccessCode();

  const [historyR, setHistoryR] = useState<IHistoryIncidentes[]>([]);

  const [search, setSearch] = useState<string>("");
  const [page, setPage] = useState<number>(0);
  const [rowsPerPage, setRowsPerPage] = useState<number>(10);

  const filteredHistory = React.useMemo(() => {
    const query = search.trim().toLowerCase();
    if (!query) return historyR;
    return historyR.filter((item) => {
      const values = [
        item?.fecha_ingreso,
        item?.num_ticket,
        item?.nestado,
        item?.descripcion,
      ];
      return values.some((v) => String(v ?? '').toLowerCase().includes(query));
    });
  }, [historyR, search]);

  const paginatedHistory = React.useMemo(() => {
    const start = page * rowsPerPage;
    return filteredHistory.slice(start, start + rowsPerPage);
  }, [filteredHistory, page, rowsPerPage]);

  const handleChangePage = (_event: React.MouseEvent<HTMLButtonElement> | null, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  
  const [openModal, setOpenModal] = useState(false);
  const [modalMessage, setModalMessage] = useState("");
  const [chatOpen, setChatOpen] = useState(false);
  const [chatMessages, setChatMessages] = useState<ChatMessage[]>([]);
  const [selectedTicket, setSelectedTicket] = useState<{num_ticket?: string | number, nestado?: string, ticket?: string} | null>(null);
  const [imageModal, setImageModal] = useState<{ open: boolean, url: string | null, num_ticket: string }>({ open: false, url: null, num_ticket: '' });
  const userData = useAppSelector((state) => state.auth.userData)
  const handleOpen = () => setOpenModal(true);
  const handleClose = () => {
    setOpenModal(false);
  };

  // Estado para modal de descripción completa
  const [descModal, setDescModal] = useState<{ open: boolean, text: string, num_ticket: string }>({ open: false, text: '', num_ticket: '' });

  const handleOpenChat = (item) => {
    setChatMessages([]);
    getMessagesTicket(item.ticket).then(data => {
      if (data) {
        const normalized: ChatMessage[] = data.map((msg: IHistoryIncidentesMessages) => ({ ...msg, tipo: String(msg.tipo) }));
        setChatMessages(normalized);
      }
    });
    setSelectedTicket({ num_ticket: item.num_ticket, nestado: item.nestado, ticket: item.ticket });
    setChatOpen(true);
  };
  const handleCloseChat = () => setChatOpen(false);

  const handleOpenImage = (item) => {
    if (item.path_photo) {
      setImageModal({ open: true, url: item.path_photo, num_ticket: item.num_ticket });
    }
  };
  const handleCloseImage = () => setImageModal({ open: false, url: null, num_ticket: '' });

  const handleSendMessage = (ticket: number, text: string) => {
    const now = new Date();
    const fecha = now.toISOString().slice(0, 16).replace('T', ' ');
    setChatMessages(prev => ([
      ...prev,
      { fecha, tipo: "3", nota: `Vecino:: ${text}` } as ChatMessage
    ]));
    sendMessageTicket({
      Usuario: userData?.usuario || '',
      Empresa: userData?.empresa || 0,
      Ticket: ticket,
      Nota: text
    });
  };

  useEffect(() => {
    (async () => {
      let historyReservas = await getHistoryIncidentes();
      if (historyReservas) {
        setHistoryR(historyReservas.reverse());
      } else {
        setModalMessage("Error al obtener el historial de incidentes");
        handleOpen();
      }
    })();
  }, []);

  useEffect(() => {
    setPage(0);
  }, [historyR, search]);

  return (
    <>
      <Box
        textAlign="center"
        display="flex"
        alignItems="center"
        flexDirection="column"
      >
        <Typography variant="h4" component="div" gutterBottom marginY="1em">
          Mis Incidentes
        </Typography>

        <Box
          display="flex"
          flexDirection="column"
          alignItems="center"
          width={0.96}
          maxWidth={1000}
        >
          <Box sx={{ width: '100%', mb: 1, display: 'flex', justifyContent: 'flex-end' }}>
            <TextField
              size="small"
              placeholder="Buscar"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Search fontSize="small" />
                  </InputAdornment>
                ),
              }}
              fullWidth={isSmallScreen}
              sx={{ maxWidth: isSmallScreen ? '100%' : 360 }}
            />
          </Box>
          <TableContainer component={Box} sx={{ width: "100%" }}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableHeaderCell sx={{ whiteSpace: "nowrap", padding: 1 }}>
                    Fecha Incidente
                  </TableHeaderCell>
                  <TableHeaderCell sx={{ whiteSpace: "nowrap", padding: 1 }}>
                    #{" "}
                  </TableHeaderCell>
                  <TableHeaderCell sx={{ whiteSpace: "nowrap", padding: 1, width: 44, maxWidth: 48 }}>
                    Estado{" "}
                  </TableHeaderCell>
                  <TableHeaderCell sx={{ whiteSpace: "nowrap", padding: 1 }}>
                    Descripcion
                  </TableHeaderCell>
                  <TableHeaderCell sx={{ whiteSpace: "nowrap", padding: 1, width: 44, maxWidth: 48 }}>
                    Acciones
                  </TableHeaderCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {paginatedHistory.length > 0 &&
                  paginatedHistory.map(item => {
                    const TableCellComponent = isSmallScreen ? MobileTableCell : MuiTableCell;

                    return (
                      <TableRow
                        key={`${item.ticket}-${item.num_ticket}-${item.fecha_ingreso}`}
                        sx={{
                        }}

                      >
                        <TableCellComponent>
                          <Box
                            component="span"
                            sx={{
                              display: 'flex',
                              alignItems: 'center',
                              gap: 1,
                              maxWidth: '100%',
                              overflow: 'hidden'
                            }}
                          >

                            {item.fecha_ingreso}
                          </Box>
                        </TableCellComponent>
                        <TableCell
                          sx={{
                            whiteSpace: "nowrap",
                            padding: 1,
                          }}
                        >
                          {item.num_ticket}
                        </TableCell>
                        <TableCell
                          sx={{
                            padding: 1,
                            width: 44,
                            maxWidth: 48,
                          }}
                        >
                          <Tooltip title={item.nestado} placement="top" arrow>
                            <Box
                              sx={{
                                maxWidth: 48,
                                display: '-webkit-box',
                                WebkitLineClamp: 2,
                                WebkitBoxOrient: 'vertical',
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                whiteSpace: 'normal',
                                wordBreak: 'break-word',
                                cursor: 'pointer',
                              }}
                            >
                              {item.nestado}
                            </Box>
                          </Tooltip>
                        </TableCell>
                        <TableCell
                          sx={{
                            padding: 1,
                          }}
                        >
                          <Tooltip title={item.descripcion} placement="top" arrow>
                            <Box
                              sx={{
                                maxWidth: 320,
                                display: '-webkit-box',
                                WebkitLineClamp: 2,
                                WebkitBoxOrient: 'vertical',
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                whiteSpace: 'normal',
                                wordBreak: 'break-word',
                                cursor: 'pointer',
                              }}
                              onClick={() => setDescModal({ open: true, text: item.descripcion, num_ticket: item.num_ticket })}
                            >
                              {item.descripcion}
                            </Box>
                          </Tooltip>
                        </TableCell>
                        <TableCell
                          sx={{
                            whiteSpace: "nowrap",
                            padding: 1,
                            width: 44,
                            maxWidth: 48,
                          }}
                        >
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, justifyContent: 'flex-start', width: '100%' }}>
                            <Button size="small" variant="text" color="primary" aria-label="Mensajes" onClick={() => handleOpenChat(item)} sx={{ minWidth: 0, p: 0.5 }}>
                              <span style={{display: 'flex', alignItems: 'center'}}>
                                <MessageIcon fontSize="small" />
                              </span>
                            </Button>
                            <Button size="small" variant="text" color="secondary" aria-label="Imágenes" onClick={() => handleOpenImage(item)} disabled={!item.path_photo} sx={{ minWidth: 0, p: 0.5 }}>
                              <span style={{display: 'flex', alignItems: 'center'}}>
                                <ImageIcon fontSize="small" />
                              </span>
                            </Button>
                          </Box>
                        </TableCell>
                      </TableRow>
                    );
                  })}
              </TableBody>
            </Table>
          </TableContainer>
          <TablePagination
            component="div"
            count={filteredHistory.length}
            page={page}
            onPageChange={handleChangePage}
            rowsPerPage={rowsPerPage}
            onRowsPerPageChange={handleChangeRowsPerPage}
            rowsPerPageOptions={[5, 10, 25, 50]}
            labelRowsPerPage="Filas por página"
          />
        </Box>

        <CustomModal openState={openModal} onCloseHandler={handleClose}>
          <Typography variant="h4" component="div" gutterBottom marginY="1em">
            {modalMessage}
          </Typography>

          <Button
            color="secondary"
            variant="outlined"
            endIcon={<Home />}
            sx={{ marginY: 2, width: "50%" }}
            onClick={handleClose}
          >
            Inicio
          </Button>
        </CustomModal>
        <ChatModal
          open={chatOpen}
          onClose={handleCloseChat}
          messages={chatMessages as ChatMessage[]}
          onSend={(text) => {
            if (selectedTicket?.ticket) {
              handleSendMessage(Number(selectedTicket.ticket), text);
            }
          }}
          ticketNumber={selectedTicket?.num_ticket}
          estado={selectedTicket?.nestado}
        />
        <Dialog open={imageModal.open} onClose={handleCloseImage} maxWidth="sm" fullWidth>
          <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            Imagen del incidente {imageModal.num_ticket}
            <IconButton aria-label="close" onClick={handleCloseImage} size="small">
              <CloseIcon fontSize="small" />
            </IconButton>
          </DialogTitle>
          <DialogContent dividers sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 300 }}>
            {imageModal.url ? (
              <img src={imageModal.url} alt="Incidente" style={{ maxWidth: '100%', maxHeight: 400, borderRadius: 8 }} />
            ) : (
              <Typography variant="body2" color="text.secondary">No hay imagen disponible.</Typography>
            )}
          </DialogContent>
        </Dialog>
        {/* Modal para mostrar la descripción completa al hacer click */}
        <Dialog open={descModal.open} onClose={() => setDescModal({ open: false, text: '', num_ticket: '' })} maxWidth="sm" fullWidth>
          <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            {"Descripción incidente " + descModal.num_ticket}
            <IconButton aria-label="close" onClick={() => setDescModal({ open: false, text: '', num_ticket: '' })} size="small">
              <CloseIcon fontSize="small" />
            </IconButton>
          </DialogTitle>
          <DialogContent dividers>
            <Typography variant="body1" sx={{ whiteSpace: 'pre-line' }}>{descModal.text}</Typography>
          </DialogContent>
        </Dialog>
      </Box>
    </>

  );
};
